import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle2, Car, MapPin, User, Clock, ArrowRight, Loader2, XCircle,
} from "lucide-react";
import { useLang } from "../i18n/LangProvider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- i18n copy — kept inline so this page has no bundled i18n dependency
const COPY = {
  en: {
    kicker: "Live tracking",
    title: "Your journey",
    poll: "Auto-refresh every 20 s",
    passenger: "Passenger",
    passengers: "Passengers",
    pickup: "Pickup",
    from: "From",
    to: "To",
    ref: "Reference",
    tap: "Tap the airplane below to see MeteoSwiss for the pickup zone.",
    stages: {
      confirmed: { label: "Confirmed", body: "Your reservation is confirmed and scheduled." },
      en_route_to_pickup: { label: "On the way", body: "Your chauffeur is en route to the pickup point." },
      arrived_at_pickup: { label: "Chauffeur at pickup", body: "Your chauffeur has arrived and is waiting." },
      passenger_on_board: { label: "On board", body: "Trip in progress — enjoy the ride." },
      completed: { label: "Completed", body: "You have arrived. Thank you for travelling with us." },
    },
    notFound: "Reservation not found. Please double-check the URL.",
    refresh: "Refresh",
  },
  de: {
    kicker: "Live-Tracking",
    title: "Ihre Fahrt",
    poll: "Auto-Aktualisierung alle 20 s",
    passenger: "Fahrgast",
    passengers: "Fahrgäste",
    pickup: "Abholung",
    from: "Von",
    to: "Nach",
    ref: "Referenz",
    tap: "Tippen Sie unten auf das Flugzeug, um MeteoSwiss für die Abholzone zu öffnen.",
    stages: {
      confirmed: { label: "Bestätigt", body: "Ihre Reservation ist bestätigt und geplant." },
      en_route_to_pickup: { label: "Unterwegs", body: "Ihre Chauffeurin ist auf dem Weg zum Abholpunkt." },
      arrived_at_pickup: { label: "Chauffeurin vor Ort", body: "Ihre Chauffeurin ist angekommen und wartet." },
      passenger_on_board: { label: "An Bord", body: "Fahrt läuft — angenehme Reise." },
      completed: { label: "Abgeschlossen", body: "Sie sind angekommen. Danke, dass Sie mit uns reisen." },
    },
    notFound: "Reservation nicht gefunden. Bitte prüfen Sie die URL.",
    refresh: "Aktualisieren",
  },
  pt: {
    kicker: "Rastreio ao vivo",
    title: "A sua viagem",
    poll: "Actualização automática a cada 20 s",
    passenger: "Passageiro",
    passengers: "Passageiros",
    pickup: "Recolha",
    from: "De",
    to: "Para",
    ref: "Referência",
    tap: "Toque no avião abaixo para ver MeteoSwiss na zona de recolha.",
    stages: {
      confirmed: { label: "Confirmada", body: "A sua reserva está confirmada e agendada." },
      en_route_to_pickup: { label: "A caminho", body: "A sua chauffeur está a caminho do ponto de recolha." },
      arrived_at_pickup: { label: "Chauffeur no local", body: "A sua chauffeur chegou e aguarda." },
      passenger_on_board: { label: "A bordo", body: "Viagem em curso — boa viagem." },
      completed: { label: "Concluída", body: "Chegaram ao destino. Obrigada por viajarem connosco." },
    },
    notFound: "Reserva não encontrada. Verifique o URL.",
    refresh: "Actualizar",
  },
  fr: {
    kicker: "Suivi en direct",
    title: "Votre trajet",
    poll: "Actualisation automatique toutes les 20 s",
    passenger: "Passager",
    passengers: "Passagers",
    pickup: "Prise en charge",
    from: "De",
    to: "Vers",
    ref: "Référence",
    tap: "Appuyez sur l'avion ci-dessous pour voir MeteoSwiss dans la zone de prise en charge.",
    stages: {
      confirmed: { label: "Confirmée", body: "Votre réservation est confirmée et planifiée." },
      en_route_to_pickup: { label: "En route", body: "Votre chauffeuse est en route vers le point de prise en charge." },
      arrived_at_pickup: { label: "Chauffeuse sur place", body: "Votre chauffeuse est arrivée et vous attend." },
      passenger_on_board: { label: "À bord", body: "Trajet en cours — bon voyage." },
      completed: { label: "Terminée", body: "Vous êtes arrivés. Merci d'avoir voyagé avec nous." },
    },
    notFound: "Réservation introuvable. Vérifiez l'URL.",
    refresh: "Actualiser",
  },
  it: {
    kicker: "Tracciamento live",
    title: "Il vostro viaggio",
    poll: "Aggiornamento automatico ogni 20 s",
    passenger: "Passeggero",
    passengers: "Passeggeri",
    pickup: "Ritiro",
    from: "Da",
    to: "A",
    ref: "Riferimento",
    tap: "Tocca l'aereo qui sotto per vedere MeteoSwiss nella zona di ritiro.",
    stages: {
      confirmed: { label: "Confermata", body: "La vostra prenotazione è confermata e pianificata." },
      en_route_to_pickup: { label: "In viaggio", body: "La vostra chauffeur è in viaggio verso il punto di ritiro." },
      arrived_at_pickup: { label: "Chauffeur sul posto", body: "La vostra chauffeur è arrivata e vi attende." },
      passenger_on_board: { label: "A bordo", body: "Viaggio in corso — buon viaggio." },
      completed: { label: "Completata", body: "Siete arrivati. Grazie per aver viaggiato con noi." },
    },
    notFound: "Prenotazione non trovata. Verificate l'URL.",
    refresh: "Aggiorna",
  },
};

const STAGE_ICONS = {
  confirmed: CheckCircle2,
  en_route_to_pickup: Car,
  arrived_at_pickup: MapPin,
  passenger_on_board: User,
  completed: CheckCircle2,
};

const ReservaStatus = () => {
  const { id } = useParams();
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reservations/${id}/status`);
      setData(res.data);
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-brand-silver" size={22} />
      </main>
    );
  }

  if (error || !data) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center px-6">
        <XCircle size={40} className="text-bronze mb-6" />
        <p className="text-brand-silver">{c.notFound}</p>
        <Link to="/" className="btn-pill mt-8">
          Home <ArrowRight size={14} />
        </Link>
      </main>
    );
  }

  const currentIdx = data.chauffeur_status_states.indexOf(data.chauffeur_status);
  const stages = data.chauffeur_status_states;

  return (
    <main className="min-h-screen pt-28 pb-24 px-6" data-testid="reserva-status-page">
      <div className="max-w-[900px] mx-auto">
        <p className="text-xs tracking-widest2 uppercase text-brand-silver mb-4">— {c.kicker}</p>
        <h1 className="display text-4xl md:text-5xl text-brand-platinum leading-[1.05] mb-3">{c.title}</h1>
        <p className="text-sm text-brand-silver">{c.poll} · {c.ref} <span className="text-brand-platinum tabular-nums">{data.short_id}</span></p>

        {/* Timeline */}
        <div className="mt-14 space-y-0" data-testid="tracking-timeline">
          {stages.map((stage, i) => {
            const done = i < currentIdx;
            const active = i === currentIdx;
            const upcoming = i > currentIdx;
            const label = c.stages[stage]?.label || stage;
            const body = c.stages[stage]?.body || "";
            const Icon = STAGE_ICONS[stage] || Clock;
            return (
              <div key={stage} data-testid={`stage-${stage}`}
                   className={`relative flex items-start gap-6 pb-10 last:pb-0`}>
                {/* Vertical line */}
                {i < stages.length - 1 && (
                  <span className={`absolute left-5 top-11 bottom-0 w-px ${done ? "bg-bronze" : "bg-brand-border"}`} />
                )}
                {/* Dot / icon */}
                <div className={`relative w-10 h-10 rounded-full flex items-center justify-center border transition-all
                  ${active
                    ? "border-bronze bg-bronze/20 text-bronze"
                    : done
                    ? "border-bronze bg-bronze text-brand-bg"
                    : "border-brand-border text-brand-silver"}`}>
                  {active && (
                    <span className="absolute inset-0 rounded-full bg-bronze/30 animate-ping" />
                  )}
                  <Icon size={16} className="relative z-10" />
                </div>
                {/* Text */}
                <div className={`flex-1 pt-1 ${upcoming ? "opacity-40" : ""}`}>
                  <p className={`text-sm tracking-widest2 uppercase ${active ? "text-bronze" : done ? "text-brand-platinum" : "text-brand-silver"}`}>
                    {label}
                  </p>
                  <p className="mt-1 text-sm text-brand-silver leading-relaxed">{body}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Trip meta */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-4">
          <MetaRow label={c.pickup} value={new Date(data.pickup_datetime).toLocaleString(lang, {
            weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit",
          })} />
          <MetaRow label={data.passengers === 1 ? c.passenger : c.passengers} value={data.passengers} />
          {data.origin && <MetaRow label={c.from} value={data.origin} />}
          {data.destination && <MetaRow label={c.to} value={data.destination} />}
          {data.flight_number && <MetaRow label="Flight" value={data.flight_number} />}
          {data.hotel_name && <MetaRow label="Hotel" value={data.hotel_name} />}
          {data.contact_name_masked && <MetaRow label={c.passenger} value={data.contact_name_masked} />}
        </div>

        <button onClick={load} data-testid="tracking-refresh"
                className="btn-pill mt-12">
          {c.refresh} <ArrowRight size={14} />
        </button>
      </div>
    </main>
  );
};

const MetaRow = ({ label, value }) => (
  <div className="border-b border-brand-border/50 pb-3">
    <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-1">{label}</p>
    <p className="text-sm text-brand-platinum">{value}</p>
  </div>
);

export default ReservaStatus;
